import { useRef, type PointerEvent, type RefObject } from 'react';

const PHONE = '(max-width: 639px)';
/** Dragged further than this (px) or faster than the velocity: the sheet closes. */
const CLOSE_DISTANCE = 110;
const CLOSE_VELOCITY = 0.6;
const EXIT_MIN_MS = 180;
const EXIT_MAX_MS = 320;

/** Rubber band: pulling a sheet upwards gets harder the further you go (iOS). */
export function rubberBand(offset: number): number {
  return offset >= 0 ? offset : -Math.sqrt(-offset) * 3;
}

/**
 * Drag-to-close for bottom sheets on phones. Spread the returned handlers on
 * the handle and header of the sheet; the sheet follows the finger via the
 * CSS variable `--sheet-drag`, which the exit animation starts from. Pulling
 * upwards meets resistance, a short pull snaps back with a spring.
 */
export function useSheetDrag(ref: RefObject<HTMLElement | null>, onClose: () => void) {
  const drag = useRef<{ y: number; t: number; lastY: number; lastT: number; id: number } | null>(
    null,
  );

  const set = (px: number) => ref.current?.style.setProperty('--sheet-drag', `${px}px`);

  return {
    onPointerDown(e: PointerEvent<HTMLElement>) {
      if (!window.matchMedia?.(PHONE).matches || !ref.current) return;
      if ((e.target as HTMLElement).closest('button, a, input, select, textarea')) return;
      drag.current = {
        y: e.clientY,
        t: e.timeStamp,
        lastY: e.clientY,
        lastT: e.timeStamp,
        id: e.pointerId,
      };
      e.currentTarget.setPointerCapture(e.pointerId);
      ref.current.style.transition = 'none';
    },
    onPointerMove(e: PointerEvent<HTMLElement>) {
      const d = drag.current;
      if (!d || d.id !== e.pointerId) return;
      d.lastY = e.clientY;
      d.lastT = e.timeStamp;
      set(rubberBand(e.clientY - d.y));
    },
    onPointerUp(e: PointerEvent<HTMLElement>) {
      const d = drag.current;
      const sheet = ref.current;
      if (!d || d.id !== e.pointerId || !sheet) return;
      drag.current = null;
      const distance = e.clientY - d.y;
      const velocity = (e.clientY - d.lastY) / Math.max(1, e.timeStamp - d.lastT);
      sheet.style.transition = '';
      if (distance > CLOSE_DISTANCE || (distance > 20 && velocity > CLOSE_VELOCITY)) {
        // Leave at the speed of the finger: the faster the swipe, the shorter the exit.
        const remaining = Math.max(0, sheet.offsetHeight - Math.max(0, distance));
        const ms = Math.min(
          EXIT_MAX_MS,
          Math.max(EXIT_MIN_MS, remaining / Math.max(velocity, 0.3)),
        );
        sheet.style.setProperty('--sheet-out-duration', `${Math.round(ms)}ms`);
        onClose();
      } else {
        set(0);
      }
    },
    onPointerCancel() {
      drag.current = null;
      if (ref.current) ref.current.style.transition = '';
      set(0);
    },
  };
}
